/* eslint-disable */
/**
 * COPIED VERBATIM from src/app/components/direct/data/carrierSchema.js (commit 7772aebb).
 *
 * Per-carrier credential fields in each carrier's own vocabulary — Bluedart says Licence key,
 * Delhivery says API token. Normalising those to a generic 'API key' is how a merchant pastes
 * the wrong value into the right-looking box, so the labels stay as the carrier writes them.
 *
 * The 'serviceLine' fields are Ashvini's service-line concept, which this module does not have;
 * the connect form filters them out rather than editing this file.
 */
/**
 * Prozo Direct — per-carrier credential field schemas (TRD M3.3).
 *
 * Replaced by: a `carrierFieldSchema` config served from the backend, derived from the
 * existing MongoCourierConfig structure.
 *
 * Two rules encoded here, both deliberate:
 *
 * 1. Labels use the CARRIER'S OWN vocabulary. Bluedart says "Licence key", Delhivery
 *    says "API token". Normalising these to a generic "API key" is how a merchant ends
 *    up pasting the wrong value into the right-looking box.
 * 2. Every field carries a `demo` value. The review build prefills the form so the whole
 *    flow — test, save, serviceability pull, account live — can be walked end to end
 *    without anyone owning real carrier credentials. Prefilled values are flagged in the
 *    UI as demo data, never presented as though they came from the carrier.
 */

// Realistic shapes, deliberately invalid as real secrets: right length and character
// class so field validation is exercised, but not resembling any live key.
const DEMO_TOKEN_40 = 'a1f4c8e2b96d47035ae81cf2760b39d4e5a7c018'
const DEMO_LICENCE_64 = 'k3Xb9Qm2Rt7Yw1Ec5Nh8Jd4Gs6Zp0Lv2Af7Uo1Ix9Kq3Bn5Tr8Wy2Ce6Mg4Hs0Dj7'

export const carrierSchemas = {
  DELHIVERY: {
    carrier: 'Delhivery',
    onboardingEmailSubject: 'Delhivery API Credentials — Production Access',
    fields: [
      {name: 'accountName', label: 'Account name', required: true, type: 'text', help: 'Yours to name. We suggest CARRIER_SERVICE_PAYMENT.', demo: 'DL_SURFACE_PREPAID'},
      {name: 'apiToken', label: 'API token', required: true, type: 'password', help: '40 characters, from the Delhivery API credentials email', demo: DEMO_TOKEN_40},
      {name: 'clientName', label: 'Client name', required: true, type: 'text', help: 'Exactly as Delhivery has it on your contract', demo: 'NEEMANS RETAIL PVT LTD'},
      {name: 'pickupName', label: 'Registered pickup name', required: true, type: 'text', help: 'The pickup name registered with Delhivery, not your Proship location name', demo: 'NEEMANS-BLR-WH1'},
      {name: 'shippingMode', label: 'Shipping mode', required: true, type: 'select', options: ['Surface', 'Express'], demo: 'Surface'},
      {name: 'serviceSubType', label: 'Service sub-type', required: false, type: 'select', options: ['FLASH_B2C_SURFACE', 'BULK_B2C', 'HEAVY'], demo: 'FLASH_B2C_SURFACE'},
      {name: 'orderNumberAs', label: 'Order number sent as', required: false, type: 'select', options: ['Reference number', 'Channel order ID'], demo: 'Reference number'},
      {name: 'serviceLine', label: 'Service line', required: true, type: 'serviceLine', help: 'Decides which mode setting governs this account', demo: 'D2C_SURFACE'},
      {name: 'environment', label: 'Environment', required: true, type: 'select', options: ['Production', 'Sandbox'], demo: 'Production'},
    ],
  },
  BLUEDART: {
    carrier: 'Bluedart',
    onboardingEmailSubject: 'Bluedart Web Service Credentials — Licence Key',
    fields: [
      {name: 'accountName', label: 'Account name', required: true, type: 'text', help: 'Yours to name. We suggest CARRIER_SERVICE_PAYMENT.', demo: 'BD_AIRETAIL_PREPAID'},
      {name: 'licenceKey', label: 'Licence key', required: true, type: 'password', help: '64 characters, starts with a letter', demo: DEMO_LICENCE_64},
      {name: 'loginId', label: 'Login ID', required: true, type: 'text', demo: 'NEEM014285'},
      {name: 'prepaidCustomerCode', label: 'Prepaid customer code', required: true, type: 'text', demo: '551246'},
      {name: 'codCustomerCode', label: 'COD customer code', required: false, type: 'text', help: 'Leave blank if prepaid only', demo: ''},
      {name: 'originArea', label: 'Origin area', required: true, type: 'text', help: "Bluedart's three-letter hub code for your pickup city", demo: 'BLR'},
      {name: 'productCode', label: 'Product code', required: true, type: 'select', options: ['ETAIL_PREPAID_AIR', 'ETAIL_COD_AIR', 'DARTPLUS_PREPAID', 'DARTPLUS_COD'], demo: 'ETAIL_PREPAID_AIR'},
      {name: 'serviceLine', label: 'Service line', required: true, type: 'serviceLine', demo: 'D2C_AIR'},
      {name: 'environment', label: 'Environment', required: true, type: 'select', options: ['Production', 'Sandbox'], demo: 'Production'},
    ],
  },
  XPRESSBEES: {
    carrier: 'Xpressbees',
    onboardingEmailSubject: 'Xpressbees API Onboarding — Client Credentials',
    fields: [
      {name: 'accountName', label: 'Account name', required: true, type: 'text', demo: 'XB_SURFACE_COD'},
      {name: 'email', label: 'Registered email', required: true, type: 'text', help: 'The login email on your Xpressbees account', demo: 'logistics@neemans.example'},
      {name: 'password', label: 'Password', required: true, type: 'password', help: 'Xpressbees issues a token against this on every call', demo: 'Xb#9f2Kd7Qa4'},
      {name: 'businessAccountId', label: 'Business account ID', required: true, type: 'text', demo: 'XBBA0094127'},
      {name: 'serviceLine', label: 'Service line', required: true, type: 'serviceLine', demo: 'D2C_SURFACE'},
      {name: 'environment', label: 'Environment', required: true, type: 'select', options: ['Production', 'Sandbox'], demo: 'Production'},
    ],
  },
  EKART: {
    carrier: 'Ekart',
    onboardingEmailSubject: 'Ekart Logistics — Merchant Integration Keys',
    fields: [
      {name: 'accountName', label: 'Account name', required: true, type: 'text', demo: 'EK_HEAVY_PREPAID'},
      {name: 'merchantCode', label: 'Merchant code', required: true, type: 'text', help: 'Ekart calls this the vendor code on some contracts', demo: 'EKNEEM0071'},
      {name: 'clientId', label: 'Client ID', required: true, type: 'text', demo: 'ekart-neemans-prod'},
      {name: 'clientSecret', label: 'Client secret', required: true, type: 'password', demo: 'e7c31b9a4f0d82165c9e'},
      {name: 'serviceLine', label: 'Service line', required: true, type: 'serviceLine', demo: 'HEAVY_BULKY'},
      {name: 'environment', label: 'Environment', required: true, type: 'select', options: ['Production', 'Sandbox'], demo: 'Sandbox'},
    ],
  },
  DTDC: {
    carrier: 'DTDC',
    onboardingEmailSubject: 'DTDC API Access — Customer Code and Token',
    fields: [
      {name: 'accountName', label: 'Account name', required: true, type: 'text', demo: 'DTDC_SURFACE_PREPAID'},
      {name: 'customerCode', label: 'Customer code', required: true, type: 'text', demo: 'BL9427'},
      {name: 'accessToken', label: 'Access token', required: true, type: 'password', help: 'From the DTDC API access email', demo: 'dtdc_9f4b2c81e7a05d63'},
      {name: 'serviceTypeId', label: 'Service type', required: true, type: 'select', options: ['B2C SMART EXPRESS', 'B2C PRIORITY', 'GROUND EXPRESS'], demo: 'B2C SMART EXPRESS'},
      {name: 'serviceLine', label: 'Service line', required: true, type: 'serviceLine', demo: 'D2C_SURFACE'},
      {name: 'environment', label: 'Environment', required: true, type: 'select', options: ['Production', 'Sandbox'], demo: 'Production'},
    ],
  },
}

export const carrierKeys = Object.keys(carrierSchemas)

// Build a prefilled form state for a carrier, so the flow is walkable immediately.
export const demoValuesFor = (carrierKey) => {
  const schema = carrierSchemas[carrierKey]
  if (!schema) return {}
  return schema.fields.reduce((acc, f) => {
    acc[f.name] = f.demo ?? ''
    return acc
  }, {})
}
